import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Sparkles, Check, X as XIcon, AlertCircle, Loader2 } from 'lucide-react';
import { improveMessage, type ImproveMessageContext } from '../../services/improveMessageApi';

/*
 * MessageAssistToolbar
 * --------------------
 * Renders a message <textarea> (passed in as children) with two circular
 * icon buttons overlaid in its top-right corner:
 *   1. **Dictate** (Mic icon) — Web Speech API speech-to-text. While
 *      recording the button turns blue with radiating "sonar" wave rings
 *      and the transcript (both interim and final) is streamed live into
 *      the textarea so the user can see what's being heard.
 *   2. **Polish with AI** (Sparkles icon) — POST /improve_message_via_gemini.
 *
 * Both icons ship with a custom, animated hover tooltip explaining what
 * they do (native `title` was too slow and too plain).
 *
 * Ancillary UI — active-listening indicator, inline errors, AI-polished
 * suggestion card — renders BELOW the textarea so it doesn't push the
 * icons out of place or fight the textarea's scroll region.
 *
 * The parent must reserve horizontal space inside the textarea so the
 * user's text doesn't collide with the icons: pass an inline
 * `style={{ paddingRight: '96px' }}` (or wider) on the textarea itself.
 */

// ---------------------------------------------------------------------------
// Minimal Web Speech API surface (browsers vary; TS ships no built-ins).
// ---------------------------------------------------------------------------
interface MinimalSpeechRecognitionEvent {
  resultIndex: number;
  results: {
    length: number;
    [i: number]: {
      isFinal: boolean;
      [j: number]: { transcript: string };
    };
  };
}

interface MinimalSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: MinimalSpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionCtor = new () => MinimalSpeechRecognition;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export interface MessageAssistToolbarProps {
  value: string;
  onChange: (next: string) => void;
  context: ImproveMessageContext;
  /**
   * Disables both actions (used while the parent form is submitting).
   * Recording is auto-stopped when this flips to true.
   */
  disabled?: boolean;
  /** Optional class for the outermost wrapper. */
  className?: string;
  /**
   * The <textarea> element the buttons should overlay. Passing the
   * textarea as children lets this component wrap it in a positioned
   * container without the parent having to manage layout by hand.
   */
  children: React.ReactNode;
}

// Small utility component — hover tooltip anchored above an icon button.
// Kept inline (not exported) because it's tightly coupled to the two
// pill-shaped icons below and doesn't need to be reused elsewhere yet.
const IconTooltip: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="group relative">
    {children}
    <span
      role="tooltip"
      className={
        // opacity-0 by default; the wrapping `.group` node lets us flip
        // it visible on hover / focus-within without JS. `pointer-events-
        // none` prevents the tooltip from creating a hover flicker when
        // the mouse crosses into it.
        'pointer-events-none absolute bottom-full right-0 mb-2 ' +
        'whitespace-nowrap rounded-md bg-navy-950/95 px-2.5 py-1.5 ' +
        'text-[11px] font-medium text-white shadow-xl ring-1 ring-white/10 ' +
        'opacity-0 translate-y-1 transition-all duration-150 ' +
        'group-hover:opacity-100 group-hover:translate-y-0 ' +
        'group-focus-within:opacity-100 group-focus-within:translate-y-0'
      }
    >
      {label}
      {/* Tiny caret pointing down at the button. */}
      <span
        aria-hidden
        className="absolute -bottom-1 right-3 h-2 w-2 rotate-45 bg-navy-950/95 ring-1 ring-white/10"
      />
    </span>
  </div>
);

export const MessageAssistToolbar: React.FC<MessageAssistToolbarProps> = ({
  value,
  onChange,
  context,
  disabled,
  className,
  children,
}) => {
  // ---- speech recognition ------------------------------------------------
  const SRCtor = useMemo(() => getSpeechRecognitionCtor(), []);
  const speechSupported = SRCtor !== null;

  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState('');

  const recognitionRef = useRef<MinimalSpeechRecognition | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // The "stable text" for the current recording session: everything that
  // has been committed so far (the textarea's value at recording start
  // PLUS every final STT chunk received during this session). We rebuild
  // the textarea's display value on every `onresult` event by concatenating
  // this with the current interim transcript, so the user sees the words
  // appear live *inside* the textarea as they speak.
  const stableTextRef = useRef<string>('');

  // Cleanup on unmount so a mid-utterance abort doesn't leak the
  // browser's underlying recognition session.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  // Kill an active recording if the parent flips disabled on (usually
  // because the form is submitting).
  useEffect(() => {
    if (disabled && isRecording) {
      recognitionRef.current?.stop();
    }
  }, [disabled, isRecording]);

  const startRecording = () => {
    if (!SRCtor || isRecording || disabled) return;
    setMicError('');

    let recognition: MinimalSpeechRecognition;
    try {
      recognition = new SRCtor();
    } catch {
      setMicError('Speech recognition failed to start on this browser.');
      return;
    }
    recognition.lang =
      (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    // Snapshot the textarea's current value — everything the user has
    // already typed is preserved verbatim; STT text is appended.
    stableTextRef.current = valueRef.current;

    recognition.onresult = (event) => {
      // Walk every result that's new in *this* event. Anything before
      // `resultIndex` was already surfaced in an earlier event.
      let finalChunk = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const alt = event.results[i][0];
        if (event.results[i].isFinal) {
          finalChunk += alt.transcript;
        } else {
          interim += alt.transcript;
        }
      }

      // Commit any final text to the stable buffer with a sensible
      // whitespace separator — no leading space against the start of a
      // fresh textarea, exactly one space between existing text and the
      // new chunk.
      if (finalChunk) {
        const base = stableTextRef.current;
        const needsSpace = base.length > 0 && !/\s$/.test(base);
        stableTextRef.current = base + (needsSpace ? ' ' : '') + finalChunk.replace(/^\s+/, '');
      }

      // Live display value = stable text + current (uncommitted) interim.
      // Feeding this into the parent's onChange makes the words appear
      // inside the textarea as the user speaks — the entire point of
      // "you can see what's being written down".
      const base = stableTextRef.current;
      let display = base;
      if (interim) {
        const needsSpace = base.length > 0 && !/\s$/.test(base);
        display = base + (needsSpace ? ' ' : '') + interim.replace(/^\s+/, '');
      }
      onChangeRef.current(display);
    };

    recognition.onerror = (event) => {
      const map: Record<string, string> = {
        'not-allowed': 'Microphone access was blocked. Please allow it in your browser and try again.',
        'service-not-allowed':
          'Microphone access was blocked. Please allow it in your browser and try again.',
        'no-speech': "Didn't catch that — try again a little closer to the mic.",
        'audio-capture': 'No microphone was found on this device.',
        network: 'Speech recognition is offline right now. Please try again.',
      };
      const msg = event?.error && map[event.error];
      setMicError(msg || 'Speech recognition failed. Please try again.');
      setIsRecording(false);
    };

    recognition.onend = () => {
      // Commit whatever's currently in the textarea as the final value.
      // Any lingering interim from the last frame is already there; we
      // just need to make sure our stableTextRef matches so a follow-up
      // recording session doesn't clobber it.
      stableTextRef.current = valueRef.current;
      setIsRecording(false);
      recognitionRef.current = null;
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
    } catch {
      setMicError('Please wait a moment and try again.');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
  };

  const toggleRecording = () => (isRecording ? stopRecording() : startRecording());

  // ---- AI polish ---------------------------------------------------------
  const [isImproving, setIsImproving] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [improveError, setImproveError] = useState('');

  const requestImprovement = async () => {
    if (isImproving || disabled) return;
    const trimmed = value.trim();
    if (trimmed.length < 5) {
      setImproveError('Type at least a few words first, then I can polish it.');
      return;
    }

    setImproveError('');
    setSuggestion(null);
    setIsImproving(true);
    try {
      const res = await improveMessage({ text: trimmed, context });
      if (res.status === 'success' && res.data && typeof res.data.improved_text === 'string') {
        setSuggestion(res.data.improved_text);
      } else {
        setImproveError(res.message || 'Could not improve the message. Please try again.');
      }
    } finally {
      setIsImproving(false);
    }
  };

  const applySuggestion = () => {
    if (suggestion != null) {
      onChange(suggestion);
    }
    setSuggestion(null);
  };

  const dismissSuggestion = () => {
    setSuggestion(null);
  };

  // ---- Styling helpers ---------------------------------------------------
  // Circular icon buttons anchored to the textarea's top-right corner.
  // `relative` on the button gives the sonar wave rings something to
  // absolutely position against. The palette below assumes a LIGHT
  // (white) textarea — icons sit as pale-navy chips that darken on
  // hover so they read clearly against the crisp white input.
  const iconBtn =
    'relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const iconBtnIdle =
    'bg-white text-navy-700 border-navy-200 shadow-sm hover:bg-navy-50 hover:border-navy-300 hover:text-navy-900';
  // Recording is BLUE (not red) so it reads as "active" rather than
  // "danger / disabled". Solid sky-500 pops crisply against white.
  const iconBtnRecording =
    'bg-sky-500 text-white border-sky-400 shadow-[0_0_16px_2px_rgba(56,189,248,0.55)]';
  const iconBtnBusy =
    'bg-accent-500 text-white border-accent-400 shadow-lg';

  // Mic tooltip label — swaps to "Click to stop" while recording so the
  // user knows how to end the session.
  const micTooltipLabel = isRecording
    ? 'Click to stop dictating'
    : 'Use microphone to speak';

  const polishTooltipLabel = isImproving
    ? 'Polishing your message…'
    : 'Polish this message with AI';

  return (
    <div className={className}>
      {/* Wrapper anchors the two floating icon buttons to the child
          textarea's top-right corner. The parent is expected to give
          the textarea enough right-padding (~96px) so typed text and
          the placeholder don't slide under the icons. */}
      <div className="message-assist relative">
        {children}
        <div className="relative sm:absolute sm:top-2.5 sm:right-2.5 flex justify-end items-center gap-2 z-10 mt-2 sm:mt-0">
          {speechSupported && (
            <IconTooltip label={micTooltipLabel}>
              {/* Sonar wave rings — rendered as siblings of the button
                  (inside the same tooltip wrapper) so they can extend
                  BEYOND the button's edges when they scale up. Only
                  rendered while recording so idle buttons are perfectly
                  round. */}
              {isRecording && (
                <>
                  {[0, 0.5, 1].map((delay) => (
                    <motion.span
                      key={delay}
                      aria-hidden
                      className="pointer-events-none absolute inset-0 rounded-full border-2 border-sky-300/70"
                      initial={{ scale: 1, opacity: 0.75 }}
                      animate={{ scale: [1, 2.1, 2.1], opacity: [0.75, 0, 0] }}
                      transition={{
                        duration: 1.6,
                        delay,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  ))}
                </>
              )}
              <button
                type="button"
                onClick={toggleRecording}
                disabled={disabled}
                aria-pressed={isRecording}
                aria-label={micTooltipLabel}
                className={`${iconBtn} ${isRecording ? iconBtnRecording : iconBtnIdle}`}
              >
                {/* Same Mic icon in both states — colour + halo tells
                    the user what state we're in without changing the
                    metaphor. A tiny inner pulse for extra life while
                    recording. */}
                <Mic className="w-4 h-4 relative z-10" />
                {isRecording && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-sky-300/25"
                    animate={{ opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
              </button>
            </IconTooltip>
          )}

          <IconTooltip label={polishTooltipLabel}>
            <button
              type="button"
              onClick={requestImprovement}
              disabled={disabled || isImproving}
              aria-label={polishTooltipLabel}
              className={`${iconBtn} ${isImproving ? iconBtnBusy : iconBtnIdle}`}
            >
              {isImproving ? (
                <Loader2 className="w-4 h-4 relative z-10 animate-spin" />
              ) : (
                // Colour is inherited from the button (navy-700 idle,
                // white while busy) so the icon always contrasts with
                // its own background regardless of state.
                <Sparkles className="w-4 h-4 relative z-10" />
              )}
            </button>
          </IconTooltip>
        </div>
      </div>

      {/* Compact status line under the textarea. While the mic is on we
          nudge the user with a small "Listening…" indicator so they
          understand the words currently appearing in the box came from
          the mic (rather than magic). */}
      {isRecording && (
        <p
          className="mt-2 text-xs text-sky-700 flex items-center gap-2"
          aria-live="polite"
        >
          <motion.span
            aria-hidden
            className="inline-block w-2 h-2 rounded-full bg-sky-500"
            animate={{ opacity: [0.35, 1, 0.35] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          Listening — speak naturally, your words are appearing above.
        </p>
      )}

      {/* Inline errors — mic + polish share a single row so the layout
          doesn't jitter when both are absent. */}
      {(micError || improveError) && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {micError || improveError}
        </p>
      )}

      {/* AI-polished suggestion preview — animates in below the textarea. */}
      <AnimatePresence initial={false}>
        {suggestion !== null && (
          <motion.div
            key="suggestion-card"
            initial={{ opacity: 0, y: -6, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="mt-3 overflow-hidden"
          >
            <div className="rounded-xl border border-accent-200 bg-gradient-to-br from-accent-50 to-indigo-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-500 text-white shadow-sm">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs font-semibold text-navy-800 uppercase tracking-wide">
                  AI-polished version
                </p>
                <span className="text-[10px] text-navy-500 font-medium">
                  · Gemini
                </span>
              </div>

              <p className="text-sm text-navy-900 leading-relaxed whitespace-pre-wrap">
                {suggestion}
              </p>

              <p className="mt-2 text-[11px] text-navy-500">
                A light polish — grammar, clarity, tone. Nothing was added
                that you didn't already say.
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-navy-900 text-white border border-navy-900 shadow-md hover:bg-navy-800 transition-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  Use this
                </button>
                <button
                  type="button"
                  onClick={dismissSuggestion}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-white text-navy-700 border border-navy-300 hover:bg-navy-50 hover:border-navy-400 transition-all"
                >
                  <XIcon className="w-3.5 h-3.5" />
                  Keep mine
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
